import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Popover, Tooltip } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { colors } from 'src/components/app-ui'
import { portalPageSlugCheckService } from 'src/api/collection'
import useStyles from './styles'

/** Design dialog: title (+ subtitle), close button, body, actions row. */
export const Dialog = ({ title, subtitle, onClose, children, actions }) => {
  const classes = useStyles()

  useEffect(() => {
    const onKey = event => event.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return ReactDOM.createPortal(
    <div className={classes.dialogBackdrop} onMouseDown={onClose}>
      <div
        className={classes.dialog}
        role="dialog"
        aria-modal="true"
        onMouseDown={event => event.stopPropagation()}
      >
        <div className={classes.dialogHead}>
          <div>
            <div className={classes.dialogTitle}>{title}</div>
            {subtitle && <div className={classes.dialogSub}>{subtitle}</div>}
          </div>
          <button
            type="button"
            className={classes.dialogClose}
            onClick={onClose}
            aria-label="Close"
          >
            <Icon icon="tabler:x" width={18} height={18} />
          </button>
        </div>
        {children}
        <div className={classes.dialogActions}>{actions}</div>
      </div>
    </div>,
    document.body,
  )
}

/** Remove (left, optional), Cancel and the primary button. */
export const DialogActions = ({
  onRemove,
  onCancel,
  onSubmit,
  submitLabel,
  submitDisabled,
}) => {
  const classes = useStyles()
  return (
    <>
      {onRemove && (
        <button type="button" className={classes.btnDanger} onClick={onRemove}>
          <Icon icon="tabler:trash" width={15} height={15} />
          Remove
        </button>
      )}
      <span className={classes.spacer} />
      <button type="button" className={classes.btnOutline} onClick={onCancel}>
        Cancel
      </button>
      <button
        type="button"
        className={classes.btnPrimary}
        onClick={onSubmit}
        disabled={submitDisabled}
      >
        {submitLabel}
      </button>
    </>
  )
}

/** Small 30×17 switch with a label, used inside dialogs. */
export const MiniToggle = ({ checked, onChange, children }) => {
  const classes = useStyles()
  return (
    <button
      type="button"
      className={classes.miniToggle}
      onClick={() => onChange(!checked)}
    >
      <span
        className={classes.miniTrack}
        style={{ background: checked ? colors.primary : colors.disabled }}
      >
        <span className={classes.miniKnob} style={{ left: checked ? 15 : 2 }} />
      </span>
      {children}
    </button>
  )
}

/**
 * "Add ▾" button with a two-option dropdown. The dropdown is a Popover (drawn
 * on the page, like RowMenu's), so a card with clipped corners can't cut it.
 */
export const AddMenu = ({ options }) => {
  const classes = useStyles()
  const [anchor, setAnchor] = useState(null)
  const close = () => setAnchor(null)

  return (
    <div className={classes.addWrap}>
      <button
        type="button"
        className={classes.addButton}
        onClick={event => setAnchor(anchor ? null : event.currentTarget)}
      >
        <Icon icon="tabler:plus" width={14} height={14} />
        Add
        <Icon icon="tabler:chevron-down" width={14} height={14} />
      </button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            marginTop: 6,
            background: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        <div className={classes.addMenu} style={{ position: 'static' }}>
          {options.map(option => (
            <button
              key={option.title}
              type="button"
              className={classes.addOption}
              onClick={() => {
                close()
                option.onClick()
              }}
            >
              <span style={{ display: 'inline-flex', color: option.color }}>
                <Icon icon={option.icon} width={18} height={18} />
              </span>
              <span>
                <span className={classes.addOptionTitle}>{option.title}</span>
                <span className={classes.addOptionDesc}>
                  {option.description}
                </span>
              </span>
            </button>
          ))}
        </div>
      </Popover>
    </div>
  )
}

/** "More" button with Edit / Remove. */
export const RowMenu = ({ editLabel, onEdit, onChangeLabel, onRemove }) => {
  const classes = useStyles()
  const [anchor, setAnchor] = useState(null)
  const run = action => () => {
    setAnchor(null)
    action()
  }

  return (
    <>
      <button
        type="button"
        title="More"
        className={classes.moreButton}
        onClick={event => setAnchor(event.currentTarget)}
      >
        <Icon icon="tabler:dots-vertical" width={16} height={16} />
      </button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.rowMenu, elevation: 0 }}
      >
        {onEdit && (
          <button
            type="button"
            className={classes.rowMenuItem}
            onClick={run(onEdit)}
          >
            <Icon icon="tabler:pencil" width={15} height={15} />
            {editLabel}
          </button>
        )}
        {onChangeLabel && (
          <button
            type="button"
            className={classes.rowMenuItem}
            onClick={run(onChangeLabel)}
          >
            <Icon icon="tabler:forms" width={15} height={15} />
            Change label
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            className={`${classes.rowMenuItem} ${classes.rowMenuDanger}`}
            onClick={run(onRemove)}
          >
            <Icon icon="tabler:trash" width={15} height={15} />
            Remove
          </button>
        )}
      </Popover>
    </>
  )
}

/**
 * List row: grip (when sortable), icon, label + note + target, then `right`.
 * Rows passed to SortableList get the drag handlers through `dragProps`.
 */
export const ListRow = ({
  icon,
  label,
  note,
  info,
  target,
  onClick,
  right,
  dragProps,
  className,
}) => {
  const classes = useStyles()
  return (
    <div className={`${classes.row} ${className || ''}`} {...dragProps?.row}>
      {dragProps ? (
        <span
          title="Drag to reorder"
          className={classes.grip}
          {...dragProps.handle}
        >
          <Icon icon="tabler:grip-vertical" width={16} height={16} />
        </span>
      ) : (
        <span className={classes.gripPlaceholder} />
      )}
      <span className={classes.rowIcon}>
        <Icon icon={icon} width={16} height={16} />
      </span>
      <div
        className={classes.rowMain}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        <div className={classes.rowLabel}>
          {label}
          {note && <span className={classes.rowNote}>{note}</span>}
          {info && (
            <Tooltip title={info} placement="top" arrow>
              <span
                className={classes.rowInfo}
                aria-label={info}
                onClick={event => event.stopPropagation()}
              >
                <Icon icon="tabler:info-circle" width={16} height={16} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={classes.rowTarget}>{target}</div>
      </div>
      {right}
    </div>
  )
}

/**
 * Drag-to-reorder for a list of rows. `renderRow(item, index, dragProps)`
 * spreads `dragProps` into a ListRow; `onMove(from, to)` gets the indexes.
 */
export const SortableList = ({ items, getKey, onMove, renderRow }) => {
  const classes = useStyles()
  const armed = useRef(false)
  const [dragIndex, setDragIndex] = useState(null)
  const [overIndex, setOverIndex] = useState(null)

  const reset = () => {
    armed.current = false
    setDragIndex(null)
    setOverIndex(null)
  }

  return items.map((item, index) =>
    React.cloneElement(
      renderRow(item, index, {
        row: {
          draggable: true,
          onDragStart: event => {
            if (!armed.current) {
              event.preventDefault()
              return
            }
            event.dataTransfer.effectAllowed = 'move'
            setDragIndex(index)
          },
          onDragOver: event => {
            if (dragIndex === null) return
            event.preventDefault()
            if (overIndex !== index) setOverIndex(index)
          },
          onDrop: event => {
            event.preventDefault()
            if (dragIndex !== null && dragIndex !== index)
              onMove(dragIndex, index)
            reset()
          },
          onDragEnd: reset,
        },
        handle: {
          onMouseDown: () => {
            armed.current = true
          },
          onMouseUp: () => {
            armed.current = false
          },
        },
      }),
      {
        key: getKey(item),
        className:
          dragIndex === index
            ? classes.rowDragging
            : overIndex === index && dragIndex !== null
            ? classes.rowOver
            : '',
      },
    ),
  )
}

export const move = (list, from, to) => {
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

const HEX = /^#[0-9a-f]{6}$/i

const luminance = hex => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!match) return 1
  const [r, g, b] = [0, 2, 4].map(i => {
    const c = parseInt(match[1].substr(i, 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export const contrastRatio = (a, b) => {
  const x = luminance(a)
  const y = luminance(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

/** White or black, whichever reads better on `background`. */
export const readableText = background =>
  contrastRatio(background, '#FFFFFF') >= contrastRatio(background, '#000000')
    ? '#FFFFFF'
    : '#000000'

/** Swatches + hex input. Calls onChange with an uppercase #RRGGBB. */
export const ColorField = ({ label, value, swatches, onChange }) => {
  const classes = useStyles()
  const [draft, setDraft] = useState(null)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className={classes.fieldTitle}>{label}</div>
      <div className={classes.colorRow}>
        {swatches.map(hex => (
          <button
            key={hex}
            type="button"
            title={hex}
            className={classes.swatch}
            style={{
              background: hex,
              boxShadow:
                hex.toUpperCase() === (value || '').toUpperCase()
                  ? `0 0 0 2px #fff, 0 0 0 4px ${colors.primary}`
                  : 'none',
            }}
            onClick={() => {
              setDraft(null)
              onChange(hex.toUpperCase())
            }}
          />
        ))}
        <div className={classes.hexField}>
          <span className={classes.hexChip} style={{ background: value }} />
          <input
            className={classes.hexInput}
            value={draft ?? value}
            onChange={event => {
              const text = event.target.value.trim()
              setDraft(text)
              const hex = text.startsWith('#') ? text : `#${text}`
              if (HEX.test(hex)) onChange(hex.toUpperCase())
            }}
            onBlur={() => setDraft(null)}
          />
        </div>
      </div>
    </div>
  )
}

/** Low-contrast warning with a one-click fix. */
export const ContrastWarning = ({ background, text, what, onFix }) => {
  const classes = useStyles()
  if (contrastRatio(background, text) >= 4.5) return null
  const suggestion = readableText(background)
  return (
    <div className={classes.contrast}>
      <span style={{ display: 'inline-flex', marginTop: 2 }}>
        <Icon icon="tabler:alert-triangle" width={16} height={16} />
      </span>
      <span>
        {what} text may be hard to read on this background.{' '}
        <a
          href="#fix"
          onClick={event => {
            event.preventDefault()
            onFix(suggestion)
          }}
        >
          Use {suggestion === '#FFFFFF' ? 'white' : 'black'} text
        </a>
      </span>
    </div>
  )
}

/** Full http(s) or mailto: address, as the menu and footer links take. */
export const isLinkUrl = value =>
  /^https?:\/\/[^\s.]+\.[^\s]+$/i.test((value || '').trim()) ||
  /^mailto:[^\s@]+@[^\s@]+\.[^\s]+$/i.test((value || '').trim())

export const isWebUrl = value =>
  /^https?:\/\/[^\s.]+\.[^\s]+$/i.test((value || '').trim())

/** "About us!" → "about-us" (the URL a new page gets from its name). */
export const slugify = text =>
  (text || '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)

/** While typing in a URL field: lowercase, dashes, no leading dash. */
export const cleanSlugInput = text =>
  (text || '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-/, '')
    .slice(0, 60)

const SLUG_FORMAT = /^[a-z0-9]+(-[a-z0-9]+)*$/

/**
 * Availability of a custom page URL, checked on the server (other pages,
 * reserved paths, course links) after a short pause in typing.
 * Returns { checking, problem }.
 */
export const useSlugCheck = (slug, pageId, skip) => {
  const [state, setState] = useState({ checking: false, problem: null })

  useEffect(() => {
    if (skip || !slug) {
      setState({ checking: false, problem: null })
      return undefined
    }
    if (!SLUG_FORMAT.test(slug)) {
      setState({
        checking: false,
        problem: 'Use lowercase letters, numbers and dashes.',
      })
      return undefined
    }
    let cancelled = false
    setState({ checking: true, problem: null })
    const timer = setTimeout(() => {
      portalPageSlugCheckService(slug, pageId)
        .then(response => {
          if (cancelled) return
          const data = response?.data || {}
          setState({
            checking: false,
            problem: data.available === false ? data.message : null,
          })
        })
        .catch(() => !cancelled && setState({ checking: false, problem: null }))
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [slug, pageId, skip])

  return state
}

export const errorMessage = (error, fallback) =>
  error?.response?.data?.operationMessage ||
  error?.response?.data?.errors?.[0] ||
  fallback
